// Shared types for phases 2–5 (plans, help desk, analytics, onboarding, settings).
// Single source of truth — the worker returns these shapes; the UI imports them. Do not fork.

// ── Plans (P2) ──
export interface Plan {
  id: string;
  name: string;
  price: string | null;
  groupKey: string | null;   // HighLevel plan grouping; related plans share this
  tierOrder: number;         // ordering within the group
  saasProducts: string[];    // canonical saasProduct slugs included
  offered: boolean;          // agency toggle
}
export interface PlanContext {
  current: { id: string; name: string } | null;
  group: string | null;
  upgrades: Array<{ id: string; name: string; price: string | null; adds: string[] }>;
  downgrades: Array<{ id: string; name: string; price: string | null }>;
}
export interface GatingRow { area: string; saasProduct: string | null; }

// content items now carry plan-lock info (bridge integrates plan-aware)
export interface ContentItem {
  type: 'doc' | 'video' | 'tour';
  id: string;               // docSlug / flowId / tour id
  title: string;
  description?: string | null;
  url?: string;             // doc/video url
  poster?: string;          // video poster
  src?: string;             // video clip src
  area?: string;
  locked?: boolean;
  requiresPlan?: { id: string; name: string } | null;
}
export interface ContextContent { area: string | null; docs: ContentItem[]; videos: ContentItem[]; tours: ContentItem[]; }

// ── Help desk (P3) ──
export interface TicketMeta { id: string; kind: 'status' | 'priority' | 'category'; label: string; color: string | null; sortOrder: number; isDefault: boolean; }
export interface Ticket {
  id: string;
  /** Friendly per-agency number (#1042). The UUID is never shown to a human. */
  ticket_number?: number | null;
  subject: string;
  body: string | null;
  status_id: string | null;
  priority_id: string | null;
  category_id: string | null;
  assigned_to: string | null;      // ss_agency_users.id; null = generic pot
  location_id: string | null;
  requester_name: string | null;
  requester_email: string | null;
  requester_user_id: string | null;
  context_area: string | null;
  context_route: string | null;
  feature_group: string | null;
  feature: string | null;
  created_at: string;
  updated_at: string;
}

// ── Smart routing (prioritized rules) ──
export type RoutingRuleType = 'feature_group' | 'saas_plan' | 'subaccount';
export interface RoutingRule {
  id: string; priority: number; rule_type: RoutingRuleType; match_value: string;
  agency_user_id: string | null; label: string | null; active: boolean;
}
// Feature-group → features taxonomy (for the ticket picker + routing match values).
export interface FeatureGroup { key: string; label: string; features: Array<{ key: string; label: string }>; }

// ── Changelog intelligence + ticket matching ──
export type FeatureLifecycle = 'roadmap' | 'labs' | 'released' | 'known_issue';
export interface FeatureWatch {
  id: string; title: string; summary: string | null; feature_group: string | null;
  lifecycle: FeatureLifecycle; confidence: string | null; source_url: string | null; released_at: string | null;
}
export interface FeatureTicketMatch {
  id: string; feature_id: string; ticket_id: string; score: number; reason: string | null;
  status: 'suggested' | 'dismissed' | 'notified'; created_at: string;
  ticket?: { subject: string; requester_name: string | null; status: string | null; created_at: string } | null;
}
export interface FeatureWithMatches { feature: FeatureWatch; matches: FeatureTicketMatch[] }

// ── Doc-intelligence expansion (relationship graph + health) ──
export interface EntityRelation { id: string; from_pk: string; to_pk: string | null; to_ref: string | null; relation: string; }
export interface EntityHealth { id: string; entity_pk: string | null; kind: string; severity: 'info' | 'warn' | 'error'; detail: string | null; resolved: boolean; }
export interface SystemGraph { nodes: SAEntity[]; edges: EntityRelation[]; health: EntityHealth[]; }
export interface TicketComment { id: string; ticket_id: string; author_type: 'agent' | 'customer' | 'system'; author_name: string | null; body: string; is_internal: boolean; loom_url?: string | null; created_at: string; }
// Rich context captured at submit time (browser/screen/route/feature) — surfaced on the agency ticket view.
export interface TicketContext {
  userAgent?: string; browser?: string; os?: string;
  screen?: { w: number; h: number }; viewport?: { w: number; h: number }; dpr?: number;
  url?: string; referrer?: string; route?: string; featureGroup?: string; area?: string;
  language?: string; capturedAt?: string;
}
export interface TicketAttachment { id: string; ticket_id: string; kind: 'screenshot' | 'file'; filename: string | null; content_type: string | null; size: number | null; url: string; created_at: string; }
// What the requester viewed/watched before opening the ticket (agency-side only).
export interface ViewedContent { contentId: string; title: string; type: 'doc' | 'video' | 'tour'; area: string | null; views: number; totalSeconds: number; lastAt: string; }
// Full ticket for the agency detail view.
export interface TicketDetailFull { ticket: Ticket; comments: TicketComment[]; context: TicketContext | null; attachments: TicketAttachment[]; viewedContent: ViewedContent[]; }
export interface AgencyUser { id: string; name: string; email: string | null; role: 'owner' | 'admin' | 'agent' | 'contractor'; active: boolean; rate_cents?: number | null; }

// ── Requester / sub-account context on the agency ticket view ──
export interface RequesterContext {
  subAccount: {
    location_id: string | null;
    client_name: string | null;
    plan: { id: string; name: string; price: string | null } | null;
    assignedAgent: { id: string; name: string } | null;
    pageUrl: string | null;          // where the requester was (context_meta.url) — powers the hotlink
    hotlink: string | null;          // resolved "open in sub-account" URL (+ #ss-agent=<ticketId>)
  };
  subAccountBurden: { totalTickets: number; open: number; resolved: number; last30d: number; avgResolutionHours: number | null };
  userBurden: { user_id: string | null; name: string | null; email: string | null; totalTickets: number; open: number };
  recentTickets: Array<{ id: string; subject: string; status: string | null; created_at: string }>;
}

// ── Time tracking ──
export interface TimeEntry {
  id: string; ticket_id: string; agent_id: string | null; agent_name: string | null;
  provider: string; started_at: string; ended_at: string | null;
  seconds: number; running: boolean; paused: boolean;
  billable: boolean; rate_cents: number | null; note: string | null;
  integrity?: TimeIntegrity | null;
}
// A single activity heartbeat the agent drawer emits while its timer runs.
export interface TimeActivity { entry_id: string; at: string; location_id: string | null; route: string | null; active: boolean; visible: boolean; kind: 'heartbeat' | 'navigate' | 'idle' | 'resume'; }
// Backend-derived time-integrity summary for an entry (spot a clock left running / an agent who wandered).
export interface TimeIntegrity {
  heartbeats: number;
  idleSeconds: number;        // clock ran but no interaction / tab hidden
  offAccountSeconds: number;  // clock ran while on a DIFFERENT sub-account than the ticket
  taskSwitches: number;       // count of sub-account changes during the entry
  activePct: number | null;   // interacted-share of the tracked time (0..1)
  flags: Array<'idle' | 'high_idle' | 'off_account' | 'no_activity'>;
}
// The agent's single active timer (drawer + console restore it).
export interface RunningTimer { entry: TimeEntry; ticketId: string; ticketSubject: string; elapsedSeconds: number; paused: boolean; locationId: string | null; }
export interface TimeProvider { provider: string; enabled: boolean; config: Record<string, unknown> | null; }
export interface TimeReportRow {
  key: string; label: string;                    // agent / ticket / sub-account depending on groupBy
  entries: number; totalSeconds: number; billableSeconds: number; billableCents: number;
}
export interface TimeReport { groupBy: 'agent' | 'ticket' | 'subaccount'; rows: TimeReportRow[]; totals: { totalSeconds: number; billableSeconds: number; billableCents: number }; }

// ── Agent drawer card (injected agent-mode; SSO-authed) — the "most valuable ticket info" ──
export interface AgentTicketCard {
  ticket: { id: string; subject: string; body: string | null; status: string | null; priority: string | null; created_at: string };
  requester: { name: string | null; email: string | null; location_id: string | null; client_name: string | null; plan: string | null };
  context: TicketContext | null;
  timer: RunningTimer | null;
}
export interface ClientAssignment { location_id: string; agency_user_id: string | null; client_name: string | null; }

// ── Analytics (P4) ──
export interface Analytics {
  contentViews: Array<{ area: string; count: number }>;
  toursCompleted: number;
  ticketsByArea: Array<{ area: string; count: number }>;
  ticketsByAssignee: Array<{ name: string; count: number }>;
  planNudges: { shown: number; clicked: number };
  topContent: Array<{ id: string; title: string; count: number }>;
  totals: { widgetOpens: number; ticketsCreated: number; docViews: number; videoPlays: number };
}

// ── Change intelligence (Pro+) ──
// The documented asset vocabulary. Superset of PatchyHub's 16 scan types (2026-07-31) — theirs
// plus the container children they expand on write (funnel_step, pipeline_stage) and the
// account-level objects we document that they don't (user, integration, phone_number, domain).
export type EntityType =
  | 'workflow' | 'form' | 'survey' | 'funnel' | 'funnel_step' | 'website'
  | 'custom_field' | 'custom_value' | 'custom_object'
  | 'pipeline' | 'pipeline_stage' | 'calendar' | 'calendar_group'
  | 'tag' | 'trigger_link' | 'email_template' | 'snippet'
  | 'membership_product' | 'conversational_ai' | 'product'
  | 'user' | 'integration' | 'phone_number' | 'domain';
export interface SAEntity {
  id: string; location_id: string; entity_type: EntityType; entity_id: string;
  name: string | null; status: string | null; ghl_version: number | null;
  latest_version: number; updated_at: string | null;
  source: 'api' | 'intercept' | 'import' | 'scan' | 'app'; managed: boolean;
  change_count?: number; last_change_at?: string | null;
}
export interface EntitySystemMap { location_id: string; counts: Record<string, number>; entities: SAEntity[]; }
// A structured diff between two captured definitions.
export interface ChangeSet { added: string[]; removed: string[]; modified: string[]; }
export interface EntityChange {
  id: string; location_id: string; entity_type: EntityType; entity_name: string | null;
  from_version: number | null; to_version: number; author_id: string | null; author_name: string | null;
  summary: string | null; diff: ChangeSet | null; created_at: string;
}
export interface EntityVersion { id: string; version: number; ghl_version: number | null; author_name: string | null; captured_at: string; hash: string | null; }
// Payload the injected interceptor posts on a builder save.
export interface SnapshotInput {
  location_id: string; entity_type: EntityType; entity_id: string; name?: string;
  ghl_version?: number; definition: unknown; author_id?: string; author_name?: string;
}
export interface Announcement {
  id: string; location_id: string | null; title: string; body: string | null;
  source: 'manual' | 'platform_changelog' | 'entity_change'; entity_ref: string | null;
  audience: 'clients' | 'team' | 'all'; status: 'draft' | 'published'; published_at: string | null; created_at: string;
}
export interface WhatsNewItem { id: string; kind: 'platform' | 'announcement' | 'change'; title: string; body: string | null; at: string; badge?: string | null; }

// ── Account documentation + risk audit (2026-07-31 — see ACCOUNT-DOCUMENTATION.md) ──

// A sub-account row in the registry: identity + widget control + documentation state.
export type AccountClass = 'client' | 'agency';
export type AccountPurpose =
  | 'saas_brand' | 'support' | 'community' | 'demo' | 'snapshot'
  | 'template' | 'internal' | 'testing' | 'partner' | 'sandbox';

export interface SubAccount {
  location_id: string;
  client_name: string | null;
  /** The agency's own statement about this account. null = unclassified (a real state). */
  account_class: AccountClass | null;
  account_purpose: AccountPurpose | null;
  plan_name: string | null;
  status: 'active' | 'uninstalled';
  widget_enabled: boolean;
  current_plan_id: string | null;
  assigned_to: string | null;
  installed_at: string | null;
  last_scan_at: string | null;
  asset_count: number;
  risk_counts: { critical: number; high: number; medium: number; low: number; info: number };
  charter_done: boolean;
}

export type ScanStatus = 'running' | 'complete' | 'partial' | 'failed';
export type CollectorStatus = 'ok' | 'error' | 'unavailable' | 'forbidden' | 'skipped';
export type ScanLane = 'api' | 'app' | 'mixed';

export interface ScanItem {
  id: string; collector: string; lane: 'api' | 'app'; status: CollectorStatus;
  fetched: number; stored: number; reason: string | null; http_status: number | null; ms: number | null;
  /** Human label resolved from the collector catalog server-side. */
  label?: string;
}
export interface AccountScan {
  id: string; location_id: string; status: ScanStatus; lane: ScanLane;
  asset_count: number; relation_count: number; fact_count: number; risk_count: number;
  coverage_ok: number; coverage_total: number;
  started_by: string | null; error: string | null;
  started_at: string; completed_at: string | null;
  items?: ScanItem[];
}

export type FactDomain =
  | 'billing' | 'messaging' | 'email' | 'integrations' | 'users' | 'limits'
  | 'compliance' | 'config' | 'snapshot';
export interface AccountFact {
  id: string; domain: FactDomain; key: string; label: string | null;
  value: unknown; captured_at: string;
}

export type RiskSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info';
export type RiskCategory =
  | 'billing' | 'messaging' | 'email' | 'integrations' | 'security'
  | 'automation' | 'snapshot' | 'config';
export type RiskStatus = 'open' | 'acknowledged' | 'resolved' | 'muted';
export interface RiskFinding {
  id: string; rule_id: string; category: RiskCategory; severity: RiskSeverity;
  title: string; detail: string | null; fix: string | null;
  evidence: unknown; entity_pk: string | null; entity_name?: string | null;
  status: RiskStatus; note: string | null;
  first_seen_at: string; last_seen_at: string; resolved_at: string | null;
}

// One step of a workflow, normalized. This is our depth advantage: PatchyHub stores
// `total_steps`; we keep every step, typed and ordered, so it can be listed AND diagrammed.
export interface AssetStep {
  id: string; order: number; type: string; name: string | null;
  parent_key?: string | null; next?: string | null; branch?: string | null;
  summary: string | null;                 // one human line: what this step actually does
  links?: Array<{ type: EntityType; ref: string; name: string | null }>;
}
// An edge out of (or into) an asset, resolved for display.
export interface AssetLink {
  relation: string; direction: 'out' | 'in';
  entity_pk: string | null; entity_type: EntityType | null;
  name: string | null; ref: string | null; resolved: boolean;
}
export interface AssetDetail {
  entity: SAEntity;
  fields: Array<{ label: string; value: string | null }>;
  steps: AssetStep[];
  triggers: AssetStep[];
  links: AssetLink[];
  risks: RiskFinding[];
  origin: { snapshot_id: string | null; origin_type: string | null } | null;
}

export interface AccountDocumentation {
  location_id: string;
  scan: AccountScan | null;
  counts: Record<string, number>;
  entities: SAEntity[];
  edges: EntityRelation[];
  facts: AccountFact[];
  risks: RiskFinding[];
  charter: AccountCharter | null;
}

export interface AccountCharter {
  account_kind: 'agency' | 'business' | 'snapshot' | 'demo' | 'other' | null;
  version_management: 'yes' | 'no' | null;
  audiences: Array<'prospective_clients' | 'current_or_former_clients' | 'internal_team' | 'other'>;
  answered_by: string | null;
  answered_at: string | null;
}

export type DriftState = 'matched' | 'missing' | 'modified' | 'extra';
export interface SnapshotDeployment {
  id: string; location_id: string; client_name: string | null; version_label: string | null;
  matched: number; missing: number; modified: number; extra: number;
  drift: Array<{ originId: string; name: string | null; type: string; state: DriftState }>;
  deployed_at: string | null; last_checked_at: string;
}
export interface SnapshotRecord {
  id: string; snapshot_id: string | null; name: string; version_label: string | null;
  source_location_id: string | null; asset_count: number; notes: string | null;
  captured_at: string | null; created_at: string;
  deployments?: SnapshotDeployment[];
}

// ── Onboarding + settings (P5) ──
export interface Onboarding { step: number; branding_cloned: boolean; voice_cloned: boolean; elevenlabs_voice_id: string | null; completed_at: string | null; }
export interface AgencySettings {
  widget_enabled: boolean;
  widget_docs_enabled: boolean;
  widget_videos_enabled: boolean;
  widget_tours_enabled: boolean;
  widget_tickets_enabled: boolean;
  widget_plans_enabled: boolean;
  widget_ftux_enabled: boolean;
  widget_position: string;
  widget_theme: 'dark' | 'light';
  upgrade_url: string | null;
  default_billable: boolean;
  subaccount_url_template: string | null;
  live_support_mode: string | null;
  live_support_url: string | null;
  // Delivery (part of the main settings save — no side effects)
  coverage_mode: 'grey_now' | 'white_only';
  staleness_mode: 'branded_wins' | 'current_wins';
  brand_docs: boolean;
  brand_screenshots: boolean;
  white_videos: boolean;
  // Agency Console look (console | studio | hq) — docs/2026-08-05-agency-console-themes.md
  agency_theme: string;
  // Voice + response-only meta (voice saved via its own action; never send the key back)
  elevenlabs_voice_id: string | null;
  elevenlabs_key_set?: boolean;
  voice_allowed?: boolean;
  common_voice_id?: string | null;
  product_tier?: ProductTier;
}
export interface Agency {
  agency_id: string;
  name: string;
  /** What their white-labeled HighLevel is called — the product a sub-account user sees. */
  app_name: string | null;
  /** The agency ENTITY (e.g. "FullStack Coordination"). Not interchangeable with app_name:
   *  narration names the product in some lines and the company in others. */
  business_name: string | null;
  /** Which HighLevel theme they run, so recordings match what their users actually see. */
  hl_theme: 'light' | 'dark';
  /** HighLevel Multi-Branding slot (beta). 1 = the default brand every agency has. */
  brand_slot?: number;
  logo_url: string | null;
  accent: string | null;
  help_site_url: string | null;
  video_library_url: string | null;
  product_tier: ProductTier;
}

// ── The Support Specialist product tiers (what the AGENCY buys) — gate which suite features
//    they get. Distinct from the agency's own SaaS plans (which gate their clients' content).
//    Derived from the landing page pricing (support-specialist-site/plans.js). ──
export type ProductTier = 'minimum' | 'starter' | 'pro' | 'scale';
export type TierFeature =
  | 'docs' | 'videos' | 'widget' | 'helpdesk' | 'tours' | 'plans'
  | 'whatsnew' | 'time_tracking' | 'context_help' | 'admin_tracks' | 'custom_domain';
export const TIER_ORDER: ProductTier[] = ['minimum', 'starter', 'pro', 'scale'];
export const TIER_META: Record<ProductTier, { name: string; price: number }> = {
  minimum: { name: 'Minimum', price: 7 },
  starter: { name: 'Starter', price: 47 },
  pro: { name: 'Pro', price: 97 },
  scale: { name: 'Scale', price: 147 },
};
// Cumulative. Minimum = help center only; Starter adds videos; Pro turns on the in-app support
// widget + help desk + tours + context help + time tracking; Scale adds admin/agency tracks + custom domain.
export const TIER_ENTITLEMENTS: Record<ProductTier, TierFeature[]> = {
  minimum: ['docs', 'whatsnew'],
  starter: ['docs', 'videos', 'whatsnew'],
  pro: ['docs', 'videos', 'widget', 'helpdesk', 'tours', 'plans', 'whatsnew', 'time_tracking', 'context_help'],
  scale: ['docs', 'videos', 'widget', 'helpdesk', 'tours', 'plans', 'whatsnew', 'time_tracking', 'context_help', 'admin_tracks', 'custom_domain'],
};
export function tierEntitled(tier: ProductTier, feature: TierFeature): boolean {
  return (TIER_ENTITLEMENTS[tier] ?? TIER_ENTITLEMENTS.pro).includes(feature);
}
export interface TierInfo { tier: ProductTier; name: string; price: number; features: TierFeature[]; upgradeTarget?: TierInfo | null; }

// ── Workflow change-log (app-side capture → ss_workflow_revisions) ──
export interface WorkflowStep { order?: number; id: string; type: string; name?: string; attributes?: Record<string, unknown>; }
export interface WorkflowRevision {
  id: string; workflowId: string; locationId: string | null; clientName: string | null;
  name: string | null; status: string | null; stepCount: number;
  updatedBy: string | null; updatedByName: string | null; source: string;
  changes: string[] | null; baseline: boolean; capturedAt: string | null; createdAt: string;
}
// One row per tracked workflow: its latest revision + history counts.
export interface WorkflowSummary extends WorkflowRevision {
  revisionCount: number; changeCount: number; lastChangeAt: string | null;
}
// A revision with its full step graph and the immediately prior revision's — the diff view.
export interface WorkflowRevisionDetail {
  revision: WorkflowRevision & { steps: WorkflowStep[] };
  prev: { id: string; name: string | null; status: string | null; createdAt: string; steps: WorkflowStep[] } | null;
}
